/**
 * eSIM Details Card Web Component
 *
 * Displays configurable eSIM details (ICCID, Phone Number, Last Communicated, Purchased Date)
 * with customizable styling and field ordering.
 *
 * Usage:
 * <esim-details-card
 *   iccid="..."
 *   phone-number="..."
 *   last-communicated="..."
 *   purchased-date="..."
 *   show-iccid="true"
 *   show-phone="true"
 *   show-last-communicated="true"
 *   show-purchased="true"
 *   show-transfer-button="true"
 *   iccid-order="1"
 *   phone-order="2"
 *   last-communicated-order="3"
 *   purchased-order="4"
 *   label-iccid="ICCID"
 *   label-phone="Phone Number"
 *   label-last-communicated="Last Communicated"
 *   label-purchased="Purchased on"
 *   label-transfer="Transfer"
 *   container-bg-color="#FFFFFF"
 *   container-padding="20"
 *   container-border-radius="8"
 *   title-text-color="#666666"
 *   title-text-size="14"
 *   title-text-weight="400"
 *   value-text-color="#000000"
 *   value-text-size="16"
 *   value-text-weight="500"
 *   copy-icon="content_copy"
 *   copy-icon-color="#5b4db3"
 *   copy-icon-size="20"
 *   copy-icon-position="right"
 *   transfer-button-bg="#5b4db3"
 *   transfer-button-text="#FFFFFF"
 *   transfer-button-size="14"
 * ></esim-details-card>
 */

class EsimDetailsCard extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    this.render();
  }

  static get observedAttributes() {
    return [
      "iccid",
      "phone-number",
      "last-communicated",
      "purchased-date",
      "show-iccid",
      "show-phone",
      "show-last-communicated",
      "show-purchased",
      "show-transfer-button",
      "iccid-order",
      "phone-order",
      "last-communicated-order",
      "purchased-order",
      "label-iccid",
      "label-phone",
      "label-last-communicated",
      "label-purchased",
      "label-transfer",
      "label-copied",
      "card-title",
      "card-title-text-color",
      "card-title-text-size",
      "card-title-text-weight",
      "container-bg-color",
      "container-bg-transparent",
      "container-padding",
      "container-border-radius",
      "title-text-color",
      "title-text-size",
      "title-text-weight",
      "value-text-color",
      "value-text-size",
      "value-text-weight",
      "copy-icon",
      "copy-icon-color",
      "copy-icon-size",
      "copy-icon-position",
      "transfer-button-bg",
      "transfer-button-text",
      "transfer-button-size",
      "transfer-button-border-radius",
      "transfer-button-padding",
      "compact-layout",
    ];
  }

  attributeChangedCallback() {
    this.render();
  }

  // Helper: Format date
  formatDate(isoString) {
    if (!isoString || isoString === "Loading..." || isoString === "N/A") return isoString;
    try {
      const date = new Date(isoString);
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return date.toLocaleString(undefined, { timeZone: tz, timeZoneName: "short" });
    } catch (e) {
      return isoString;
    }
  }

  // Helper: Copy to clipboard
  async copyToClipboard(text, fieldId, iconElement) {
    // Check if this icon is currently disabled
    if (iconElement && iconElement.classList.contains("copy-disabled")) {
      return;
    }

    try {
      await navigator.clipboard.writeText(text);

      // Swap icon to checkmark and disable for 1.5 seconds
      if (iconElement) {
        const originalIcon = iconElement.textContent;
        iconElement.textContent = "check";
        iconElement.classList.add("copy-disabled");

        setTimeout(() => {
          iconElement.textContent = originalIcon;
          iconElement.classList.remove("copy-disabled");
        }, 1500);
      }
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }

  // Get ordered fields
  getOrderedFields() {
    const fields = [];

    if (this.getAttribute("show-iccid") === "true") {
      fields.push({
        type: "iccid",
        order: parseInt(this.getAttribute("iccid-order") || "1"),
        label: this.getAttribute("label-iccid") || "ICCID",
        value: this.getAttribute("iccid") || "Loading...",
        hasCopy: true,
        hasTransfer: false,
      });
    }

    if (this.getAttribute("show-phone") === "true") {
      fields.push({
        type: "phone",
        order: parseInt(this.getAttribute("phone-order") || "2"),
        label: this.getAttribute("label-phone") || "Phone Number",
        value: this.getAttribute("phone-number") || "N/A",
        hasCopy: true,
        hasTransfer: this.getAttribute("show-transfer-button") === "true",
      });
    }

    if (this.getAttribute("show-last-communicated") === "true") {
      fields.push({
        type: "last-communicated",
        order: parseInt(this.getAttribute("last-communicated-order") || "3"),
        label: this.getAttribute("label-last-communicated") || "Last Communicated",
        value: this.formatDate(this.getAttribute("last-communicated") || "N/A"),
        hasCopy: false,
        hasTransfer: false,
      });
    }

    if (this.getAttribute("show-purchased") === "true") {
      fields.push({
        type: "purchased",
        order: parseInt(this.getAttribute("purchased-order") || "4"),
        label: this.getAttribute("label-purchased") || "Purchased on",
        value: this.formatDate(this.getAttribute("purchased-date") || "Loading..."),
        hasCopy: false,
        hasTransfer: false,
      });
    }

    // Sort by order
    fields.sort((a, b) => a.order - b.order);

    return fields;
  }

  render() {
    // Styling attributes
    const cardTitle = this.getAttribute("card-title") || "";
    const cardTitleTextColor = this.getAttribute("card-title-text-color") || "#000000";
    const cardTitleTextSize = this.getAttribute("card-title-text-size") || "20";
    const cardTitleTextWeight = this.getAttribute("card-title-text-weight") || "600";
    const containerBgColor = this.getAttribute("container-bg-color") || "#FFFFFF";
    const containerBgTransparent = this.getAttribute("container-bg-transparent") === "true";
    const containerPadding = this.getAttribute("container-padding") || "20";
    const containerBorderRadius = this.getAttribute("container-border-radius") || "8";
    const titleTextColor = this.getAttribute("title-text-color") || "#666666";
    const titleTextSize = this.getAttribute("title-text-size") || "14";
    const titleTextWeight = this.getAttribute("title-text-weight") || "400";
    const valueTextColor = this.getAttribute("value-text-color") || "#000000";
    const valueTextSize = this.getAttribute("value-text-size") || "16";
    const valueTextWeight = this.getAttribute("value-text-weight") || "500";
    const copyIcon = this.getAttribute("copy-icon") || "content_copy";
    const copyIconColor = this.getAttribute("copy-icon-color") || "#5b4db3";
    const copyIconSize = this.getAttribute("copy-icon-size") || "20";
    const copyIconPosition = this.getAttribute("copy-icon-position") || "right";
    const compactLayout = this.getAttribute("compact-layout") === "true";
    const transferButtonBg = this.getAttribute("transfer-button-bg") || "#5b4db3";
    const transferButtonText = this.getAttribute("transfer-button-text") || "#FFFFFF";
    const transferButtonSize = this.getAttribute("transfer-button-size") || "14";
    const transferButtonBorderRadius = this.getAttribute("transfer-button-border-radius") || "6";
    const transferButtonPadding = this.getAttribute("transfer-button-padding") || "8";
    const transferLabel = this.getAttribute("label-transfer") || "Transfer";

    const fields = this.getOrderedFields();

    this.shadowRoot.innerHTML = `
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200" />

      <style>
        :host {
          display: block;
          width: 100%;
        }

        .esim-details-card-title {
          font-size: ${cardTitleTextSize}px;
          font-weight: ${cardTitleTextWeight};
          color: ${cardTitleTextColor};
          margin: 0 0 12px 0;
        }

        .esim-details-container {
          background-color: ${containerBgTransparent ? "transparent" : containerBgColor};
          padding: ${containerPadding}px;
          border-radius: ${containerBorderRadius}px;
          display: flex;
          flex-direction: column;
          gap: 16px;
          min-width: 260px;
        }

        .esim-detail-field {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .esim-detail-title {
          font-size: ${titleTextSize}px;
          font-weight: ${titleTextWeight};
          color: ${titleTextColor};
        }

        .esim-detail-value-row {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          ${copyIconPosition === "left" ? "flex-direction: row-reverse; justify-content: flex-end;" : ""}
        }

        .esim-detail-value {
          font-size: ${valueTextSize}px;
          font-weight: ${valueTextWeight};
          color: ${valueTextColor};
          word-break: break-word;
          min-width: 0;
          ${compactLayout ? "flex: 0 0 auto;" : "flex: 1 1 auto;"}
        }

        .copy-icon {
          font-family: 'Material Symbols Outlined';
          font-size: ${copyIconSize}px;
          color: ${copyIconColor};
          cursor: pointer;
          transition: opacity 0.2s ease;
          user-select: none;
          flex-shrink: 0;
        }

        .copy-icon:hover {
          opacity: 0.7;
        }

        .copy-icon.copy-disabled {
          opacity: 1.0;
          cursor: not-allowed;
          pointer-events: none;
        }

        .transfer-button {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: ${transferButtonPadding}px ${parseInt(transferButtonPadding) * 1.5}px;
          background-color: ${transferButtonBg};
          color: ${transferButtonText};
          font-family: var(--font-body-family), sans-serif;
          font-size: ${transferButtonSize}px;
          font-weight: 500;
          border: none;
          border-radius: ${transferButtonBorderRadius}px;
          cursor: pointer;
          transition: opacity 0.2s ease;
          flex-shrink: 0;
          white-space: nowrap;
        }

        .transfer-button:hover {
          opacity: 0.9;
        }

        .transfer-button .material-symbols-outlined {
          font-size: ${parseInt(transferButtonSize) + 2}px;
        }
      </style>

      ${cardTitle ? `<h3 class="esim-details-card-title">${cardTitle}</h3>` : ""}

      <div class="esim-details-container">
        ${fields
          .map(
            (field) => `
          <div class="esim-detail-field">
            <div class="esim-detail-title">${field.label}</div>
            <div class="esim-detail-value-row">
              <div class="esim-detail-value" id="value-${field.type}">${field.value}</div>
              ${
                field.hasCopy
                  ? `<span class="copy-icon material-symbols-outlined" data-copy="${field.type}">${copyIcon}</span>`
                  : ""
              }
              ${
                field.hasTransfer
                  ? `
                <button class="transfer-button" id="transfer-button">
                  <span class="material-symbols-outlined">sync_alt</span>
                  <span>${transferLabel}</span>
                </button>
              `
                  : ""
              }
            </div>
          </div>
        `
          )
          .join("")}
      </div>
    `;

    // Add event listeners for copy icons
    this.shadowRoot.querySelectorAll(".copy-icon").forEach((icon) => {
      icon.addEventListener("click", () => {
        const fieldType = icon.getAttribute("data-copy");
        const copiedLabel = this.getAttribute("label-copied") || "Copied!";
        const value = this.shadowRoot.getElementById(`value-${fieldType}`).textContent.replace(" " + copiedLabel, "");
        this.copyToClipboard(value, `value-${fieldType}`, icon);
      });
    });

    // Add event listener for transfer button
    const transferButton = this.shadowRoot.getElementById("transfer-button");
    if (transferButton) {
      transferButton.addEventListener("click", () => {
        // Dispatch custom event that parent page can listen to
        this.dispatchEvent(
          new CustomEvent("transfer-clicked", {
            bubbles: true,
            composed: true,
            detail: {
              iccid: this.getAttribute("iccid"),
              phoneNumber: this.getAttribute("phone-number"),
            },
          })
        );
      });
    }
  }

  // Public method to update eSIM data
  updateEsimData(esimData) {
    if (esimData.iccid) this.setAttribute("iccid", esimData.iccid);
    if (esimData.phoneNumber !== undefined) this.setAttribute("phone-number", esimData.phoneNumber || "N/A");
    if (esimData.lastCommunicated) this.setAttribute("last-communicated", esimData.lastCommunicated);
    if (esimData.purchasedDate) this.setAttribute("purchased-date", esimData.purchasedDate);
  }
}

// Register the custom element
customElements.define("esim-details-card", EsimDetailsCard);
